import * as React from 'react';

import { useQuery } from '@apollo/client';

import { EnterpriseProductCardCollectionsPlaceholder } from 'bundles/enterprise-collections/components/EnterpriseProductCardCollection';
import { OrgHomeBrowseCollections as BrowseCollections } from 'bundles/enterprise-legacy-learner-home/components/single-program/BrowseCollections';
import { OrgHomeCuratedCollections as CuratedCollections } from 'bundles/enterprise-legacy-learner-home/components/single-program/CuratedCollections';
import CurriculumCollectionsQuery from 'bundles/enterprise-legacy-learner-home/components/single-program/queries/CurriculumCollections.graphql';
import type {
  CurriculumCollectionsQuery as CurriculumCollectionsQueryData,
  CurriculumCollectionsQueryVariables,
} from 'bundles/enterprise-legacy-learner-home/components/single-program/queries/__generated__/CurriculumCollections';
import { useDiscoveryBrowseCollectionsData } from 'bundles/enterprise-legacy-learner-home/components/single-program/withDiscoveryBrowseCollections';
import type { OnProductCardClick } from 'bundles/enterprise-legacy-learner-home/types/Products';
import type { CollectionListItemStruct } from 'bundles/enterprise-legacy-learner-home/types/programCommon';
import getCatalogBrowseCollections from 'bundles/enterprise-legacy-learner-home/utils/getCatalogBrowseCollection';

type PropsFromCaller = {
  programId: string;
  isAuthenticatedUser: boolean;
  programSlug: string;
  programName: string;
  onProductCardClick: OnProductCardClick;
  academicDisciplineName?: string;
  enableCurriculumBuilder?: boolean;
  allowOrgForSpecializationConfiguration?: boolean;
  hideCourseraRecommendations?: boolean;
};

type Props = PropsFromCaller;

const ProgramCollectionsViewPlaceholder = () => (
  <>
    <EnterpriseProductCardCollectionsPlaceholder />
    <EnterpriseProductCardCollectionsPlaceholder />
  </>
);

export function ProgramCollectionsViewRenderer({
  programId,
  programSlug,
  programName,
  onProductCardClick,
  academicDisciplineName,
  allowOrgForSpecializationConfiguration,
  isAuthenticatedUser,
  enableCurriculumBuilder,
  hideCourseraRecommendations,
}: Props) {
  // Design wants us to render up to 2 collections in total. If there is only one collection type, we render up to two
  // of those. Otherwise, we render at most one of each (which is also the semi-loaded state). When we lack any
  // information, be hopeful that we'll get two collections to replace the placeholders with.

  const { contextId, contextType, start, limit } = getCatalogBrowseCollections(Boolean(isAuthenticatedUser), 2);
  const { browseCollections, browseLoading, browseError } = useDiscoveryBrowseCollectionsData(
    {
      programId,
      contextId,
      contextType,
      limit,
      start,
    },
    hideCourseraRecommendations
  );

  const {
    data,
    loading: curatedLoading,
    error: curatedError,
  } = useQuery<CurriculumCollectionsQueryData, CurriculumCollectionsQueryVariables>(CurriculumCollectionsQuery, {
    ssr: false,
    variables: {
      programId,
      start: '0',
      limit: 2,
      enableCurriculumBuilder: Boolean(enableCurriculumBuilder),
      allowOrgForSpecializationConfiguration: Boolean(allowOrgForSpecializationConfiguration),
    },
    context: { clientName: 'gatewayGql' },
  });

  const curatedCollections = data?.NaptimeQueries?.ProgramCurriculumCollectionsV1Resource?.curriculumTracksByProgram
    ?.elements as CollectionListItemStruct[] | undefined;

  if (curatedLoading && browseLoading) {
    return <ProgramCollectionsViewPlaceholder />;
  }

  const noCuratedCollections = !curatedLoading && !curatedCollections?.length;
  const noBrowseCollections = !browseLoading && !browseCollections?.length;

  return (
    <>
      <CuratedCollections
        programId={programId}
        programSlug={programSlug}
        programName={programName}
        onProductCardClick={onProductCardClick}
        collections={noBrowseCollections ? curatedCollections : curatedCollections?.slice(0, 1)}
        loading={curatedLoading}
        error={Boolean(curatedError)}
        placeholderLength={noBrowseCollections ? 2 : 1}
        academicDisciplineName={academicDisciplineName}
        allowOrgForSpecializationConfiguration={allowOrgForSpecializationConfiguration}
      />
      <BrowseCollections
        programId={programId}
        programSlug={programSlug}
        programName={programName}
        onProductCardClick={onProductCardClick}
        collections={noCuratedCollections ? browseCollections : browseCollections?.slice(0, 1)}
        loading={browseLoading}
        error={Boolean(browseError)}
        placeholderLength={noCuratedCollections ? 2 : 1}
        allowOrgForSpecializationConfiguration={allowOrgForSpecializationConfiguration}
      />
    </>
  );
}

ProgramCollectionsViewRenderer.Placeholder = ProgramCollectionsViewPlaceholder;
const ProgramCollectionsView = ProgramCollectionsViewRenderer;

export default ProgramCollectionsView;
